import React from 'react';
import { Container } from 'react-bootstrap';
import { Award } from 'react-bootstrap-icons';
import { motion, useReducedMotion, useScroll, useSpring } from 'motion/react';
import DaveWilliamsPhoto from './giants-images/dave-williams.jpg';
import GaryGrayPhoto from './giants-images/gary-gray.jpg';
import JeanMarcComtoisPhoto from './giants-images/jean-marc-comtois.jpg';
import RobertaBondarPhoto from './giants-images/roberta-bondar.jpg';
import RobertThirskPhoto from './giants-images/robert-thirsk.jpg';
import StephenBlizzardPhoto from './giants-images/stephen-blizzard.jpg';

const heroStats = [
    { value: '2019', label: 'Founded', accent: '#a51e36' },
    { value: 'AsMA', label: 'Affiliate organization', accent: '#0b3d91' },
    { value: '90+', label: 'Paid members in 2025', accent: '#ffc107' },
    { value: 'CStaRS', label: 'History project', accent: '#4dacff' }
];

const timeline = [
    {
        year: '2019',
        label: 'Foundation Year',
        accent: '#0b3d91',
        entries: [
            {
                heading: 'May - Initial Strategic Meeting',
                details: [
                    'AsMA Annual Scientific Meeting in Las Vegas.',
                    'Attendees included Dr. Kate Manderson, Dr. Tarek Sardana, Dr. Pierre Morissette, Dr. Joan Saary, Dr. Adam Sirek, and others.',
                    'Dr. Manderson presented on AsMA and potential pathways for CSAM to work with the organization.'
                ]
            },
            {
                heading: 'June 2019',
                details: [
                    'CSAM was incorporated on 15 June 2019 as a Canadian not-for-profit corporation, with formal Articles of Incorporation and By-laws established.',
                    'The incorporation followed strategic discussions at the Aerospace Medical Association Annual Scientific Meeting.',
                    'CSAM created a national professional home for aerospace medicine in Canada.'
                ]
            }
        ]
    },
    {
        year: '2020',
        label: 'Recognition',
        accent: '#a51e36',
        entries: [
            {
                heading: 'Affiliate Recognition',
                details: [
                    'CSAM was recognized as an Affiliate Organization of the Aerospace Medical Association.',
                    'Dr. Marv Lange delivered the inaugural keynote lecture on 20 October 2020.'
                ]
            }
        ]
    },
    {
        year: '2021',
        label: 'Education',
        accent: '#4dacff',
        entries: [
            {
                heading: 'Educational Talks',
                details: [
                    'CSAM hosted a series of invited educational talks featuring Canadian and international leaders in aerospace medicine, including Dr. Dave Williams, Anil Ikeda, and Michael Harrison.'
                ]
            }
        ]
    },
    {
        year: '2022',
        label: 'Expansion',
        accent: '#00a99d',
        entries: [
            {
                heading: 'April 2022',
                details: [
                    'CSAM contributed to the Trauma Association of Canada Annual Scientific Meeting, strengthening national visibility.',
                    'CSAM initiated modernization of its website and digital infrastructure.',
                    'CStaRS was formally introduced to the CSAM Board, establishing a structured student and resident subgroup.'
                ]
            },
            {
                heading: 'June 2022',
                details: [
                    'Planning began for regular CSAM gatherings at AsMA, adopting a breakfast format.'
                ]
            },
            {
                heading: 'October 2022',
                details: [
                    'Initial discussions were held regarding the introduction of a paid CSAM membership model.'
                ]
            },
            {
                heading: 'November 2022',
                details: [
                    'CSAM held its Annual General Meeting.',
                    'Kendra Kincade of Elevate Aviation delivered an invited lecture on diversity, equity, and leadership in aviation and aerospace careers.'
                ]
            }
        ]
    },
    {
        year: '2023',
        label: 'Formalization',
        accent: '#ffc107',
        entries: [
            {
                heading: 'Spring-Summer 2023',
                details: [
                    'CSAM transitioned toward a formal governance structure.',
                    'The paid membership model launched, supported by a redesigned website.'
                ]
            },
            {
                heading: 'May 2023',
                details: [
                    'The CSAM Breakfast was held at AsMA, establishing a flagship annual event.',
                    'CSAM formally established the Jean-Marc Comtois Honorary Lecture.',
                    'Dr. Angus Rupert delivered the inaugural Jean-Marc Comtois Honorary Lecture.',
                    'CSAM delivered a presentation to the Medical Legal Society.'
                ]
            },
            {
                heading: 'September 2023',
                details: [
                    'CSAM confirmed sponsorship of the Aerospace Medicine Podcast.'
                ]
            },
            {
                heading: 'November 2023',
                details: [
                    'CSAM held its Annual General Meeting.',
                    'CSAM formally recognized Dr. Stephen Blizzard and Dr. Gary Gray for their lasting contributions to aerospace medicine.'
                ]
            }
        ]
    },
    {
        year: '2024',
        label: 'Development',
        accent: '#d7194a',
        entries: [
            {
                heading: 'February 2024',
                details: [
                    'CSAM expanded its communications and digital infrastructure.',
                    'CSAM supported national aerospace medicine education initiatives.'
                ]
            },
            {
                heading: 'May 2024',
                details: [
                    'The CSAM Breakfast was held at AsMA in Chicago.',
                    'LCol Erin Smith delivered the Jean-Marc Comtois Honorary Lecture.'
                ]
            },
            {
                heading: 'September 2024',
                details: [
                    'CSAM finalized updates to its By-laws and governance framework.'
                ]
            },
            {
                heading: 'November 2024',
                details: [
                    'CSAM held its Annual General Meeting.',
                    'Dr. Kathleen van Benthem and Emily Larkin delivered keynote presentations.'
                ]
            }
        ]
    },
    {
        year: '2025',
        label: 'Growth',
        accent: '#0f8f5f',
        entries: [
            {
                heading: 'February 2025',
                details: [
                    'CSAM adjudicated student travel awards, supporting trainee participation in aerospace medicine conferences.'
                ]
            },
            {
                heading: 'April 2025',
                details: [
                    'CSAM and CStaRS members took part in the Care in the Air Symposium in Toronto, which focused on healthcare delivery in aviation and extreme environments.'
                ]
            },
            {
                heading: 'June 2025',
                details: [
                    'The CSAM Breakfast was held at AsMA in Atlanta.',
                    'Dr. Kris Lehnhardt delivered the Jean-Marc Comtois Honorary Lecture.',
                    'CSAM maintained an active presence at AsMA through member participation and organizational representation.'
                ]
            },
            {
                heading: 'November 2025',
                details: [
                    'CSAM held its Annual General Meeting.',
                    'Dr. Andrew Mulloy delivered a keynote address on aerospace cardiology.',
                    'CSAM reported 90 active paid members, reflecting continued national growth.',
                    'A collaborative initiative with CStaRS was launched to document and publish the history of CSAM on the Society website.'
                ]
            }
        ]
    }
];

const giants = [
    {
        name: 'Dr. Dave Williams',
        title: 'Physician-Astronaut and Advocate for Space Health',
        focus: 'Space health, exploration medicine, and Canadian human spaceflight',
        accent: '#0b3d91',
        photo: DaveWilliamsPhoto,
        photoPositionDesktop: '48% 18%',
        photoPositionTablet: '50% 24%',
        photoPositionMobile: '50% 22%',
        summary: [
            'Dr. Williams was born in 1954 in Saskatoon, Saskatchewan, and completed a BSc in Biology, an MSc in Physiology, and an MD at McGill University.',
            'A commercial and multi-engine pilot, he was selected as a Canadian Space Agency astronaut in 1992.',
            'He served as a mission specialist on NASA STS-90 and STS-118, conducting life-science experiments and completing the first Canadian spacewalk.',
            'He also served as crew medical officer, EVA astronaut, flight engineer, aquanaut, and later Director of Space and Life Sciences at NASA Johnson Space Center.'
        ],
        awards: 'NASA Space Flight Medal; NASA Outstanding Leadership Medal; Officer of the Order of Canada; Order of Ontario; inducted into Canada\'s Aviation Hall of Fame.'
    },
    {
        name: 'Dr. Gary Gray',
        title: 'Pioneer of Canadian Aerospace Medicine and Global Space Health',
        focus: 'Aeromedical risk, medical certification, and space health standards',
        accent: '#a51e36',
        photo: GaryGrayPhoto,
        photoPositionDesktop: '50% 16%',
        photoPositionTablet: '50% 24%',
        photoPositionMobile: '50% 24%',
        summary: [
            'Dr. Gray served as an RCAF flight surgeon and completed medical training and doctoral research in altitude illness at the University of Toronto.',
            'He pioneered evidence-based aeromedical risk assessment and modernized pilot and astronaut medical certification.',
            'His work enabled safer return-to-flight decisions for pilots with diabetes, depression, cardiovascular disease, and corrected vision.',
            'He led medical screening for Canada\'s astronaut program and chaired International Space Station medical standards.'
        ],
        awards: 'Canada\'s Aviation Hall of Fame; Order of Military Merit; Ad Astra Award; multiple national and international lifetime honours.'
    },
    {
        name: 'Dr. Jean-Marc Comtois',
        title: 'Legacy Builder and Mentor in Canadian Aerospace Medicine',
        focus: 'Mentorship, mission support, and Canadian aerospace medicine culture',
        accent: '#ffc107',
        photo: JeanMarcComtoisPhoto,
        photoPositionDesktop: '52% 20%',
        photoPositionTablet: '50% 28%',
        photoPositionMobile: '50% 28%',
        summary: [
            'Dr. Comtois was a flight surgeon and medical support leader associated with the Canadian Space Agency\'s space medicine program.',
            'He provided medical support for astronaut training and missions, including CSA involvement in NASA\'s NEEMO 7 undersea space mission.',
            'He helped shape CSAM\'s values through mentorship, education, and professional leadership.'
        ],
        awards: 'The Jean-Marc Comtois Honorary Lecture reflects Dr. Comtois\' lasting role in shaping the values and professional culture of aerospace medicine in Canada.'
    },
    {
        name: 'Dr. Roberta Bondar',
        title: 'Neurologist-Astronaut and Pioneer of Space Medicine',
        focus: 'Neuroscience, balance systems, and adaptation to spaceflight',
        accent: '#00a99d',
        photo: RobertaBondarPhoto,
        photoPositionDesktop: '48% 16%',
        photoPositionTablet: '50% 23%',
        photoPositionMobile: '50% 22%',
        summary: [
            'Dr. Bondar is a neurologist and physician whose research focused on the nervous system, balance, and orientation during and after spaceflight.',
            'She served as payload specialist on STS-42 in 1992, conducting life-science experiments during the International Microgravity Laboratory mission.',
            'She studied human responses to weightlessness, including vestibular function and balance.',
            'Her work advanced understanding of long-duration spaceflight risks, including bone loss, immune dysfunction, and post-flight cerebral blood-flow abnormalities.'
        ],
        awards: 'NASA Space Medal; Canadian Medical Hall of Fame; Companion of the Order of Canada.'
    },
    {
        name: 'Dr. Robert Thirsk',
        title: 'Physician-Astronaut and Leader in Canadian Space Medicine',
        focus: 'Long-duration spaceflight, remote healthcare, and space physiology',
        accent: '#4dacff',
        photo: RobertThirskPhoto,
        photoPositionDesktop: '50% 18%',
        photoPositionTablet: '50% 24%',
        photoPositionMobile: '50% 23%',
        summary: [
            'Dr. Thirsk is a mechanical engineer, physician, and former Canadian Space Agency astronaut.',
            'He was selected in Canada\'s first astronaut recruitment class in 1983.',
            'He flew on STS-78 in 1996 and spent 188 days on the International Space Station in 2009.',
            'He advanced space physiology, remote healthcare, and Canadian leadership in space health planning.'
        ],
        awards: 'NASA Space Flight Medal; Queen Elizabeth II Diamond Jubilee Medal; Officer of the Order of Canada.'
    },
    {
        name: 'Dr. Stephen Blizzard',
        title: 'Leader in Aviation Medicine, Education, and Professional Standards',
        focus: 'Aviation medicine standards, education, and professional leadership',
        accent: '#d7194a',
        photo: StephenBlizzardPhoto,
        photoPositionDesktop: '42% 20%',
        photoPositionTablet: '50% 30%',
        photoPositionMobile: '48% 28%',
        summary: [
            'Dr. Blizzard was born in Trinidad and Tobago, immigrated to Canada in 1958, and earned his MD at the University of Western Ontario.',
            'He served as Base Surgeon and Flight Surgeon in the Canadian Armed Forces.',
            'He developed foundational aviation medicine standards, including flight times and flight duty times in Canada and patient care in flight.',
            'He represented Canada in major humanitarian and international missions and became a respected leader in aviation and aerospace medicine.'
        ],
        awards: 'Canadian Medical Hall of Fame; United Nations Peacekeeping Medal; Trinidad Icon Award; Dr. Stephen Blizzard Trailblazer Award established in his honour.'
    }
];

function slugifyProfileName(name) {
    return name
        .toLowerCase()
        .replace(/^dr\.\s*/, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
}

function TimelineCard({ item, index, prefersReducedMotion }) {
    const side = index % 2 === 0 ? 'left' : 'right';
    const panelMotion = prefersReducedMotion
        ? {}
        : {
            initial: { opacity: 0, y: 34, x: side === 'left' ? -28 : 28 },
            transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
            viewport: { amount: 0.28, once: true },
            whileInView: { opacity: 1, y: 0, x: 0 }
        };

    return (
        <div
            className={`history-legacy-timeline-item history-legacy-timeline-item-${side}`}
            style={{ '--history-accent': item.accent }}
        >
            <div className="history-legacy-timeline-marker" aria-hidden="true">
                <div className="history-legacy-year-medallion">
                    <span>{item.year}</span>
                </div>
            </div>
            <motion.article className="history-legacy-card history-legacy-timeline-panel" {...panelMotion}>
                <div className="history-legacy-timeline-panel-head">
                    <h3>{item.label}</h3>
                </div>
                <div className="history-legacy-entry-list">
                    {item.entries.map((entry) => (
                        <section className="history-legacy-entry" key={`${item.year}-${entry.heading}`}>
                            <div className="history-legacy-entry-marker" aria-hidden="true" />
                            <div>
                                <h4>{entry.heading}</h4>
                                {entry.details.map((detail) => (
                                    <p key={detail}>{detail}</p>
                                ))}
                            </div>
                        </section>
                    ))}
                </div>
            </motion.article>
        </div>
    );
}

function GiantCard({ person, index, prefersReducedMotion }) {
    const initials = person.name
        .replace('Dr. ', '')
        .split(' ')
        .map((part) => part[0])
        .join('');
    const profileId = slugifyProfileName(person.name);
    const cardMotion = prefersReducedMotion
        ? {}
        : {
            initial: { opacity: 0, y: 26 },
            transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
            viewport: { amount: 0.18, once: true },
            whileInView: { opacity: 1, y: 0 }
        };

    return (
        <motion.article
            className="history-legacy-card history-legacy-giant-card giants-profile-card"
            id={profileId}
            style={{
                '--history-accent': person.accent,
                '--profile-image-position-desktop': person.photoPositionDesktop,
                '--profile-image-position-tablet': person.photoPositionTablet,
                '--profile-image-position-mobile': person.photoPositionMobile
            }}
            {...cardMotion}
        >
            <div className="giants-profile-media" aria-hidden="true">
                <span className="giants-profile-number">{String(index + 1).padStart(2, '0')}</span>
                {person.photo ? (
                    <div className="giants-profile-photo-frame">
                        <img
                            className="giants-profile-photo"
                            src={person.photo}
                            alt=""
                        />
                    </div>
                ) : (
                    <div className="giants-profile-initials">{initials}</div>
                )}
                <span className="giants-profile-field">{person.focus.split(',')[0]}</span>
            </div>
            <div className="giants-profile-content">
                <h3>{person.name}</h3>
                <p className="giants-profile-title">{person.title}</p>
                <p className="giants-profile-focus">{person.focus}</p>
                <div className="giants-profile-summary">
                    {person.summary.map((item) => (
                        <p key={item}>{item}</p>
                    ))}
                </div>
                <div className="giants-awards-callout">
                    <span>
                        <Award />
                        Key Awards
                    </span>
                    <p>{person.awards}</p>
                </div>
            </div>
        </motion.article>
    );
}

export default function HistoryLegacyPage() {
    const prefersReducedMotion = useReducedMotion();
    const timelineRef = React.useRef(null);
    const { scrollYProgress } = useScroll({
        offset: ['start 72%', 'end 38%'],
        target: timelineRef
    });
    const railScale = useSpring(scrollYProgress, {
        damping: 24,
        mass: 0.35,
        stiffness: 92
    });

    return (
        <Container fluid className="history-legacy-page mt-100">
            <header className="history-legacy-hero">
                <div className="history-legacy-hero-inner">
                    <h1>CSAM History</h1>
                    <p>
                        A year-by-year look at the defining moments and collaborative work that shaped aerospace medicine in Canada.
                    </p>
                    <dl className="history-legacy-fact-strip" aria-label="CSAM history highlights">
                        {heroStats.map((stat) => (
                            <div className="history-legacy-fact" key={stat.label} style={{ '--history-accent': stat.accent }}>
                                <dd>{stat.value}</dd>
                                <dt>{stat.label}</dt>
                            </div>
                        ))}
                    </dl>
                </div>
            </header>

            <section className="history-legacy-section" aria-labelledby="history-timeline-title">
                <div className="history-legacy-section-heading history-legacy-section-heading-centered">
                    <div>
                        <h2 id="history-timeline-title">CSAM Timeline</h2>
                        <p>A journey through CSAM history and achievements.</p>
                    </div>
                </div>
                <div className="history-legacy-timeline" ref={timelineRef}>
                    <div className="history-legacy-spine" aria-hidden="true">
                        <motion.div
                            className="history-legacy-spine-progress"
                            style={{ scaleY: prefersReducedMotion ? 1 : railScale }}
                        />
                    </div>
                    {timeline.map((item, index) => (
                        <TimelineCard
                            index={index}
                            item={item}
                            key={item.year}
                            prefersReducedMotion={prefersReducedMotion}
                        />
                    ))}
                </div>
            </section>
        </Container>
    );
}

export function GiantsOfAerospacePage() {
    const prefersReducedMotion = useReducedMotion();

    return (
        <Container fluid className="history-legacy-page history-legacy-page-giants mt-100">
            <header className="giants-hero">
                <div className="giants-hero-inner">
                    <div className="giants-hero-copy">
                        <span className="giants-hero-kicker">Canadian Aerospace Medicine</span>
                        <h1>Giants of Aerospace Medicine</h1>
                        <p>
                            Profiles of Canadian leaders whose work shaped aviation medicine, space health,
                            education, and professional standards.
                        </p>
                    </div>
                    <nav className="giants-hero-directory" aria-label="Giants profile directory">
                        <div className="giants-directory-grid">
                            {giants.map((person) => (
                                <a
                                    href={`#${slugifyProfileName(person.name)}`}
                                    key={person.name}
                                    style={{ '--history-accent': person.accent }}
                                >
                                    <span>{person.name.replace('Dr. ', '')}</span>
                                </a>
                            ))}
                        </div>
                    </nav>
                </div>
            </header>

            <section className="history-legacy-section giants-profiles-section" aria-labelledby="history-giants-title">
                <div className="history-legacy-section-heading giants-section-heading">
                    <div>
                        <h2 id="history-giants-title">Pioneers and Leaders</h2>
                        <p>Recognizing lasting contributions to aerospace medicine in Canada.</p>
                    </div>
                </div>
                <div className="giants-profiles-grid">
                    {giants.map((person, index) => (
                        <GiantCard
                            index={index}
                            key={person.name}
                            person={person}
                            prefersReducedMotion={prefersReducedMotion}
                        />
                    ))}
                </div>
            </section>
        </Container>
    );
}
